import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { CertificateTemplatesOverrideExtended } from "services/KnowfullyAdminClient";
import CertificateTemplateProfessionForm from "./CertificateTemplateProfessionForm";
import { useIntl } from "react-intl";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";

const useStyles = makeStyles((theme: Theme) =>
  ({
    paperWrapper: {
      width: "100%",
      overflow: "auto",
      backgroundColor: theme.palette.primary.light,
    },
    paper: {
      width: "100%",
      height: "20vh",
      overflow: "auto",
      marginTop: "15px",
    },
    professionLabel: {
      paddingTop: "10px",
      fontWeight: "bold",
      color: theme.palette.primary.dark,
      textDecoration: "underline",
    },
    descriptionBox: {
      width: "90%",
      padding: "8px",
    },
    actions: {
      width: "10%",
      display: "inline-block",
      verticalAlign: "top",
      textAlign: "right",
    },
    editButton: {
      "&:hover": {
        color: "orange",
      },
    },
    deleteButton: {
      "&:hover": {
        color: "red",
      },
    },
  })
);

type CertificateTemplatesOverrideBoxProps = {
  certificateTemplateId: string;
  certificateTemplatesOverride: CertificateTemplatesOverrideExtended;
  onSavedSuccessfully: () => void;
  onDeletedSuccessfully: () => void;
  onProfessionRepeated: () => void;
};

const CertificateTemplatesOverrideBox = ({
  certificateTemplateId,
  certificateTemplatesOverride,
  onSavedSuccessfully,
  onDeletedSuccessfully,
  onProfessionRepeated,
}: CertificateTemplatesOverrideBoxProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [editOpen, editOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const { certificateService, certificateTemplateEditUrl } = useGlobalContext();

  const handleDelete = async () => {
    try {
      await registerPromise(
        certificateService.deleteCertificateTemplatesOverride(
          certificateTemplatesOverride.id!
        )
      );
      queryClient.invalidateQueries([
        "certificateTemplatesOverrides",
        certificateTemplateId,
      ]);
      onDeletedSuccessfully();
      navigate(certificateTemplateEditUrl(certificateTemplateId));
    } catch (err) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Paper sx={styles.paperWrapper}>
        <div style={{ display: "flex" }}>
          <Box sx={styles.descriptionBox}>
            <Typography variant="h6">
              {certificateTemplatesOverride.profession ??
                t({ id: "certificateCompletionLabel" })}
            </Typography>
          </Box>
          <Box sx={styles.actions}>
            <IconButton
              color="primary"
              component="span"
              sx={styles.editButton}
              onClick={editOpenActions.setTrue}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              color="primary"
              component="span"
              sx={styles.deleteButton}
              onClick={() => deleteOpenActions.setTrue()}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </div>
      </Paper>
      <br></br>
      <Dialog open={editOpen} disableEnforceFocus maxWidth="md" fullWidth>
        <DialogContent>
          <CertificateTemplateProfessionForm
            certificateTemplateId={certificateTemplateId}
            openActions={editOpenActions}
            certificateTemplatesOverride={certificateTemplatesOverride}
            onSavedSuccessfully={onSavedSuccessfully}
            onProfessionRepeated={onProfessionRepeated}
          />
        </DialogContent>
      </Dialog>
      <DeleteEntityDialog
        dialogTitleKey="certificateTemplatesOverrideDeleteTitle"
        contentKey="certificateTemplatesOverrideDeleteQuestion"
        open={deleteOpen}
        handleDelete={handleDelete}
        onCancel={deleteOpenActions.setFalse}
      />
    </>
  );
};

export default CertificateTemplatesOverrideBox;
